// src/App.js
import React, { useState, useEffect, useCallback } from "react";

const API_BASE = "http://localhost:5001";

function App() {
  const [view, setView] = useState("login");
  const [token, setToken] = useState("");
  const [user, setUser] = useState(null);

  const [authForm, setAuthForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "patient",
    specialization: "",
    age: "",
    problem: "",
    contact: "",
  });

  const [statusMsg, setStatusMsg] = useState("");

  const handleChangeAuth = (e) => {
    setAuthForm({ ...authForm, [e.target.name]: e.target.value });
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setStatusMsg("Signing up...");
    try {
      const payload = {
        name: authForm.name,
        email: authForm.email,
        password: authForm.password,
        role: authForm.role,
      };

      if (authForm.role === "doctor") {
        payload.specialization = authForm.specialization;
      }

      if (authForm.role === "patient") {
        payload.age = Number(authForm.age);
        payload.problem = authForm.problem;
        payload.contact = authForm.contact;
      }

      const res = await fetch(`${API_BASE}/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Signup failed");
      }

      setStatusMsg("Signup successful! Please login.");
      setView("login");
    } catch (err) {
      setStatusMsg(err.message);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setStatusMsg("Logging in...");
    try {
      const body = {
        email: authForm.email,
        password: authForm.password,
        role: authForm.role,
      };
      const res = await fetch(`${API_BASE}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Login failed");
      setToken(data.token);
      setUser(data.user);
      setStatusMsg("Login successful!");
      setView("dashboard");
    } catch (err) {
      setStatusMsg(err.message);
    }
  };

  const handleLogout = () => {
    setToken("");
    setUser(null);
    setView("login");
    setStatusMsg("Logged out");
  };

  return (
    <div style={styles.app}>
      <div style={styles.gradientBg} />
      <header style={styles.header}>
        <div style={styles.logoWrap}>
          <div style={styles.logoIcon}>🏥</div>
          <div>
            <h1 style={styles.title}>HMS – Hospital Management System</h1>
            <p style={styles.subtitle}>
              Smart, clean and role-based hospital dashboard
            </p>
          </div>
        </div>
        {user && (
          <div style={styles.userInfo}>
            <div style={styles.userChip}>
              <div style={styles.avatar}>
                {user.name.charAt(0).toUpperCase()}
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{user.name}</div>
                <div style={{ fontSize: 11, opacity: 0.8 }}>{user.role}</div>
              </div>
            </div>
            <button style={styles.buttonGhost} onClick={handleLogout}>
              Logout
            </button>
          </div>
        )}
      </header>

      <main style={styles.main}>
        {!user && (
          <div style={styles.splitLayout}>
            <div style={styles.heroPanel}>
              <h2 style={styles.heroTitle}>Manage your hospital in one place</h2>
              <p style={styles.heroText}>
                Role-based dashboards for admins, doctors and patients. Track
                appointments, prescriptions and availability in a smooth, modern
                interface.
              </p>
              <ul style={styles.heroList}>
                <li>✔ Separate dashboards for each role</li>
                <li>✔ Doctor availability & specialization view</li>
                <li>✔ Digital prescriptions & reports</li>
                <li>✔ JWT-secured REST APIs</li>
              </ul>
              <div style={styles.heroBadgeRow}>
                <span style={styles.badge}>MERN</span>
                <span style={styles.badgeSoft}>Real-world mini project</span>
              </div>
            </div>

            <div style={styles.authPanel}>
              {view === "login" ? (
                <AuthCard
                  mode="login"
                  authForm={authForm}
                  onChange={handleChangeAuth}
                  onSubmit={handleLogin}
                  switchToSignup={() => {
                    setStatusMsg("");
                    setView("signup");
                  }}
                />
              ) : (
                <AuthCard
                  mode="signup"
                  authForm={authForm}
                  onChange={handleChangeAuth}
                  onSubmit={handleSignup}
                  switchToLogin={() => {
                    setStatusMsg("");
                    setView("login");
                  }}
                />
              )}
            </div>
          </div>
        )}

        {user && view === "dashboard" && (
          <Dashboard token={token} user={user} />
        )}
      </main>

      {statusMsg && <div style={styles.toast}>{statusMsg}</div>}
    </div>
  );
}

function AuthCard({
  mode,
  authForm,
  onChange,
  onSubmit,
  switchToSignup,
  switchToLogin,
}) {
  const isLogin = mode === "login";
  return (
    <div style={styles.card}>
      <div style={styles.cardHeader}>
        <h2 style={{ margin: 0 }}>
          {isLogin ? "Welcome back" : "Create an account"}
        </h2>
        <p style={styles.cardSub}>
          {isLogin
            ? "Login with your role to open your dashboard."
            : "Sign up as patient, doctor or admin to start using HMS."}
        </p>
      </div>

      <div style={styles.roleTabs}>
        {["patient", "doctor", "admin"].map((role) => (
          <button
            key={role}
            type="button"
            onClick={() => onChange({ target: { name: "role", value: role } })}
            style={{
              ...styles.roleTab,
              ...(authForm.role === role ? styles.roleTabActive : {}),
            }}
          >
            {role.charAt(0).toUpperCase() + role.slice(1)}
          </button>
        ))}
      </div>

      <form onSubmit={onSubmit} style={styles.form}>
        {!isLogin && (
          <input
            style={styles.input}
            type="text"
            name="name"
            placeholder="Full Name"
            value={authForm.name}
            onChange={onChange}
            required
          />
        )}

        <input
          style={styles.input}
          type="email"
          name="email"
          placeholder="Email address"
          value={authForm.email}
          onChange={onChange}
          required
        />

        <input
          style={styles.input}
          type="password"
          name="password"
          placeholder="Password"
          value={authForm.password}
          onChange={onChange}
          required
        />

        {/* Doctor extra field in signup */}
        {authForm.role === "doctor" && !isLogin && (
          <input
            style={styles.input}
            type="text"
            name="specialization"
            placeholder="Specialization (e.g. Cardiologist)"
            value={authForm.specialization}
            onChange={onChange}
            required
          />
        )}

        {/* Patient extra fields in signup */}
        {authForm.role === "patient" && !isLogin && (
          <>
            <input
              style={styles.input}
              type="number"
              name="age"
              placeholder="Age"
              value={authForm.age}
              onChange={onChange}
              required
            />
            <input
              style={styles.input}
              type="text"
              name="problem"
              placeholder="Health problem / symptoms"
              value={authForm.problem}
              onChange={onChange}
              required
            />
            <input
              style={styles.input}
              type="text"
              name="contact"
              placeholder="Contact number"
              value={authForm.contact}
              onChange={onChange}
              required
            />
          </>
        )}

        <button type="submit" style={styles.buttonPrimary}>
          {isLogin ? "Login to Dashboard" : "Sign Up"}
        </button>
      </form>

      <p style={styles.switchText}>
        {isLogin ? "Don't have an account? " : "Already registered? "}
        <button
          type="button"
          style={styles.linkButton}
          onClick={isLogin ? switchToSignup : switchToLogin}
        >
          {isLogin ? "Sign up" : "Login"}
        </button>
      </p>
    </div>
  );
}

function Dashboard({ token, user }) {
  const [activeTab, setActiveTab] = useState(
    user.role === "admin"
      ? "patients"
      : user.role === "doctor"
      ? "appointments"
      : "myProfile"
  );

  const tabsByRole = {
    admin: [
      { id: "patients", label: "Patients" },
      { id: "doctors", label: "Doctors" },
      { id: "appointments", label: "Appointments" },
    ],
    doctor: [
      { id: "myProfile", label: "My Profile" },
      { id: "appointments", label: "Appointments" },
    ],
    patient: [
      { id: "myProfile", label: "My Profile" },
      { id: "appointments", label: "My Appointments" },
    ],
  };

  const tabs = tabsByRole[user.role] || [];

  return (
    <div style={styles.dashboardWrap}>
      <section style={styles.cardHero}>
        <div>
          <p style={styles.welcomeLabel}>Welcome,</p>
          <h2 style={styles.welcomeName}>{user.name} 👋</h2>
          <p style={styles.welcomeRole}>
            You are logged in as <strong>{user.role}</strong>. Choose a module
            below.
          </p>
        </div>
        <div style={styles.statsRow}>
          <div style={styles.statPill}>
            <span>Role</span>
            <strong>{user.role}</strong>
          </div>
          <div style={styles.statPill}>
            <span>Security</span>
            <strong>JWT Auth</strong>
          </div>
        </div>
      </section>

      <div style={styles.tabBar}>
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            style={{
              ...styles.tabButton,
              ...(activeTab === t.id ? styles.tabButtonActive : {}),
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div style={styles.modulesGrid}>
        {user.role === "admin" && activeTab === "patients" && (
          <PatientsModule token={token} />
        )}
        {user.role === "admin" && activeTab === "doctors" && (
          <DoctorsModule token={token} isDoctorView={false} />
        )}
        {user.role === "admin" && activeTab === "appointments" && (
          <AppointmentsModule token={token} user={user} />
        )}

        {user.role === "doctor" && activeTab === "myProfile" && (
          <DoctorsModule token={token} isDoctorView />
        )}
        {user.role === "doctor" && activeTab === "appointments" && (
          <AppointmentsModule token={token} user={user} />
        )}

        {user.role === "patient" && activeTab === "myProfile" && (
          <PatientsSelfModule token={token} />
        )}
        {user.role === "patient" && activeTab === "appointments" && (
          <AppointmentsModule token={token} user={user} />
        )}
      </div>
    </div>
  );
}

/* ---------- PATIENTS (ADMIN) ---------- */

function PatientsModule({ token }) {
  const [patients, setPatients] = useState([]);
  const [form, setForm] = useState({
    name: "",
    age: "",
    problem: "",
    contact: "",
  });

  const fetchPatients = async () => {
    const res = await fetch(`${API_BASE}/patients`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json();
    setPatients(data);
  };

  useEffect(() => {
    fetchPatients();
  }, []); // eslint-disable-line

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const addPatient = async (e) => {
    e.preventDefault();
    const res = await fetch(`${API_BASE}/patients`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        name: form.name,
        age: Number(form.age),
        problem: form.problem,
        contact: form.contact,
      }),
    });
    if (res.ok) {
      setForm({ name: "", age: "", problem: "", contact: "" });
      fetchPatients();
    } else {
      const err = await res.json();
      alert(err.message || "Error adding patient");
    }
  };

  const deletePatient = async (id) => {
    await fetch(`${API_BASE}/patients/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    fetchPatients();
  };

  return (
    <section style={styles.card}>
      <div style={styles.cardHeaderRow}>
        <h3 style={styles.cardTitle}>Patients Management</h3>
        <span style={styles.pillBlue}>{patients.length} records</span>
      </div>

      <form onSubmit={addPatient} style={styles.inlineForm}>
        <input
          style={styles.input}
          name="name"
          placeholder="Patient name"
          value={form.name}
          onChange={handleChange}
          required
        />
        <input
          style={styles.input}
          name="age"
          type="number"
          placeholder="Age"
          value={form.age}
          onChange={handleChange}
          required
        />
        <input
          style={styles.input}
          name="problem"
          placeholder="Problem / symptoms"
          value={form.problem}
          onChange={handleChange}
          required
        />
        <input
          style={styles.input}
          name="contact"
          placeholder="Contact number"
          value={form.contact}
          onChange={handleChange}
          required
        />
        <button style={styles.buttonPrimary} type="submit">
          + Add patient
        </button>
      </form>

      <div style={styles.table}>
        <div
          style={{
            ...styles.tableHeader,
            gridTemplateColumns: "2fr 0.7fr 1.5fr 1.5fr 0.8fr",
          }}
        >
          <span>Name</span>
          <span>Age</span>
          <span>Problem</span>
          <span>Contact</span>
          <span>Action</span>
        </div>
        {patients.map((p) => (
          <div
            key={p._id}
            style={{
              ...styles.tableRow,
              gridTemplateColumns: "2fr 0.7fr 1.5fr 1.5fr 0.8fr",
            }}
          >
            <span>{p.name}</span>
            <span>{p.age ?? "—"}</span>
            <span>{p.problem || "—"}</span>
            <span>{p.contact || "—"}</span>
            <span>
              <button
                style={styles.buttonDangerSm}
                onClick={() => deletePatient(p._id)}
              >
                Delete
              </button>
            </span>
          </div>
        ))}
        {patients.length === 0 && (
          <div style={styles.emptyState}>
            No patients yet. Add one using the form above.
          </div>
        )}
      </div>
    </section>
  );
}

/* ---------- PATIENT SELF VIEW ---------- */

function PatientsSelfModule({ token }) {
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchSelf = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/patients/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        setLoading(false);
        return;
      }
      const data = await res.json();
      setPatient(data);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchSelf();
  }, [fetchSelf]);

  return (
    <section style={styles.card}>
      <h3 style={styles.cardTitle}>My Profile</h3>

      {loading && <p style={styles.emptyState}>Loading...</p>}

      {!loading && !patient && (
        <p style={styles.emptyState}>
          No patient record yet. Admin can link your record.
        </p>
      )}

      {!loading && patient && (
        <div style={styles.profileGrid}>
          <div>
            <p style={styles.profileLabel}>Name</p>
            <p style={styles.profileValue}>{patient.name}</p>
          </div>
          <div>
            <p style={styles.profileLabel}>Age</p>
            <p style={styles.profileValue}>
              {patient.age != null ? patient.age : "Not provided"}
            </p>
          </div>
          <div>
            <p style={styles.profileLabel}>Problem</p>
            <p style={styles.profileValue}>
              {patient.problem ? patient.problem : "Not provided"}
            </p>
          </div>
          <div>
            <p style={styles.profileLabel}>Contact</p>
            <p style={styles.profileValue}>
              {patient.contact ? patient.contact : "Not provided"}
            </p>
          </div>
        </div>
      )}
    </section>
  );
}

/* ---------- DOCTORS MODULE ---------- */

function DoctorsModule({ token, isDoctorView }) {
  const [doctors, setDoctors] = useState([]);
  const [form, setForm] = useState({
    name: "",
    specialization: "",
    available: true,
  });

  const fetchDoctors = async () => {
    const res = await fetch(`${API_BASE}/doctors`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json();
    setDoctors(data);
  };

  useEffect(() => {
    fetchDoctors();
  }, []); // eslint-disable-line

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({
      ...form,
      [name]: name === "available" ? value === "true" : value,
    });
  };

  const addDoctor = async (e) => {
    e.preventDefault();
    const res = await fetch(`${API_BASE}/doctors`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(form),
    });
    if (res.ok) {
      setForm({ name: "", specialization: "", available: true });
      fetchDoctors();
    } else {
      const err = await res.json();
      alert(err.message || "Error adding doctor");
    }
  };

  return (
    <section style={styles.card}>
      <div style={styles.cardHeaderRow}>
        <h3 style={styles.cardTitle}>
          {isDoctorView ? "My Doctor Profile" : "Doctors Directory"}
        </h3>
        <span style={styles.pillPurple}>{doctors.length} doctors</span>
      </div>

      {!isDoctorView && (
        <form onSubmit={addDoctor} style={styles.inlineForm}>
          <input
            style={styles.input}
            name="name"
            placeholder="Doctor name"
            value={form.name}
            onChange={handleChange}
            required
          />
          <input
            style={styles.input}
            name="specialization"
            placeholder="Specialization"
            value={form.specialization}
            onChange={handleChange}
            required
          />
          <select
            name="available"
            value={form.available}
            onChange={handleChange}
            style={styles.input}
          >
            <option value="true">Available</option>
            <option value="false">Not available</option>
          </select>
          <button style={styles.buttonPrimary} type="submit">
            + Add doctor
          </button>
        </form>
      )}

      <div style={styles.table}>
        <div
          style={{
            ...styles.tableHeader,
            gridTemplateColumns: "2fr 2fr 1fr",
          }}
        >
          <span>Name</span>
          <span>Specialization</span>
          <span>Status</span>
        </div>
        {doctors.map((d) => (
          <div
            key={d._id}
            style={{
              ...styles.tableRow,
              gridTemplateColumns: "2fr 2fr 1fr",
            }}
          >
            <span>{d.name}</span>
            <span>{d.specialization || "Not set"}</span>
            <span>{d.available ? "Available" : "Not available"}</span>
          </div>
        ))}
        {doctors.length === 0 && (
          <div style={styles.emptyState}>
            No doctors yet. Add them from the form above.
          </div>
        )}
      </div>

      {isDoctorView && doctors.length > 0 && (
        <p style={styles.helperText}>
          This list shows how patients will see your specialization and
          availability.
        </p>
      )}
    </section>
  );
}

/* ---------- APPOINTMENTS + PRESCRIPTIONS ---------- */

function AppointmentsModule({ token, user }) {
  const [appointments, setAppointments] = useState([]);
  const [form, setForm] = useState({
    patient: "",
    doctor: "",
    date: "",
    time: "",
  });

  const [doctors, setDoctors] = useState([]);
  const [patients, setPatients] = useState([]);

  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [prescriptionText, setPrescriptionText] = useState("");
  const [reportText, setReportText] = useState("");

  const fetchAppointments = async () => {
    const res = await fetch(`${API_BASE}/appointments`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json();
    setAppointments(data);
  };

  const fetchDoctors = async () => {
    const res = await fetch(`${API_BASE}/doctors`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json();
    setDoctors(data);
  };

  const fetchPatients = async () => {
    const res = await fetch(`${API_BASE}/patients`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return;
    const data = await res.json();
    setPatients(data);
  };

  useEffect(() => {
    fetchAppointments();
    fetchDoctors();
    if (user.role === "admin") fetchPatients();
  }, []); // eslint-disable-line

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const createAppointment = async (e) => {
    e.preventDefault();
    const body = {
      patient: user.role === "patient" ? "me" : form.patient,
      doctor: form.doctor,
      date: form.date,
      time: form.time,
    };
    const res = await fetch(`${API_BASE}/appointments`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });
    if (res.ok) {
      setForm({ patient: "", doctor: "", date: "", time: "" });
      fetchAppointments();
    } else {
      const err = await res.json();
      alert(err.message || "Error creating appointment");
    }
  };

  const openPrescription = (appt) => {
    setSelectedAppointment(appt);
    setPrescriptionText(appt.prescription || "");
    setReportText(appt.report || "");
  };

  const savePrescription = async () => {
    if (!selectedAppointment) return;
    const res = await fetch(
      `${API_BASE}/appointments/${selectedAppointment._id}/prescription`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          prescription: prescriptionText,
          report: reportText,
        }),
      }
    );
    if (res.ok) {
      setSelectedAppointment(null);
      setPrescriptionText("");
      setReportText("");
      fetchAppointments();
    } else {
      const err = await res.json();
      alert(err.message || "Error saving prescription");
    }
  };

  return (
    <section style={styles.card}>
      <div style={styles.cardHeaderRow}>
        <h3 style={styles.cardTitle}>Appointments & Prescriptions</h3>
        <span style={styles.pillGreen}>{appointments.length} appointments</span>
      </div>

      {user.role !== "doctor" && (
        <form onSubmit={createAppointment} style={styles.inlineForm}>
          {user.role === "admin" && (
            <select
              name="patient"
              value={form.patient}
              onChange={handleChange}
              style={styles.input}
              required
            >
              <option value="">Select patient</option>
              {patients.map((p) => (
                <option key={p._id} value={p._id}>
                  {p.name}
                </option>
              ))}
            </select>
          )}

          {user.role === "patient" && (
            <input
              style={{ ...styles.input, background: "#f1f5ff" }}
              value="Patient: You"
              disabled
            />
          )}

          <select
            name="doctor"
            value={form.doctor}
            onChange={handleChange}
            style={styles.input}
            required
          >
            <option value="">Select doctor</option>
            {doctors.map((d) => (
              <option key={d._id} value={d._id}>
                {d.name} ({d.specialization || "N/A"})
              </option>
            ))}
          </select>

          <input
            style={styles.input}
            name="date"
            type="date"
            value={form.date}
            onChange={handleChange}
            required
          />
          <input
            style={styles.input}
            name="time"
            type="time"
            value={form.time}
            onChange={handleChange}
            required
          />
          <button style={styles.buttonPrimary} type="submit">
            + Book
          </button>
        </form>
      )}

      <div style={styles.table}>
        <div
          style={{
            ...styles.tableHeader,
            gridTemplateColumns: "1.5fr 1.5fr 1fr 1fr 1.5fr",
          }}
        >
          <span>Patient</span>
          <span>Doctor</span>
          <span>Date</span>
          <span>Time</span>
          <span>Prescription / Report</span>
        </div>

        {appointments.map((a) => (
          <div
            key={a._id}
            style={{
              ...styles.tableRow,
              gridTemplateColumns: "1.5fr 1.5fr 1fr 1fr 1.5fr",
            }}
          >
            <span>{a.patient?.name || "N/A"}</span>
            <span>
              {a.doctor?.name || "N/A"}{" "}
              {a.doctor?.specialization && (
                <span style={styles.tagLight}>{a.doctor.specialization}</span>
              )}
            </span>
            <span>{a.date}</span>
            <span>{a.time}</span>
            <span>
              {a.prescription || a.report ? (
                <button
                  style={styles.buttonGhostSm}
                  onClick={() => openPrescription(a)}
                >
                  View / Edit
                </button>
              ) : user.role === "doctor" ? (
                <button
                  style={styles.buttonPrimarySm}
                  onClick={() => openPrescription(a)}
                >
                  Add
                </button>
              ) : (
                <span style={styles.mutedText}>Not added yet</span>
              )}
            </span>
          </div>
        ))}
        {appointments.length === 0 && (
          <div style={styles.emptyState}>No appointments yet.</div>
        )}
      </div>

      {selectedAppointment && (
        <div style={styles.modalBackdrop}>
          <div style={styles.modal}>
            <h3 style={styles.cardTitle}>Prescription & Report</h3>
            <p style={styles.modalSubtitle}>
              Patient:{" "}
              <strong>{selectedAppointment.patient?.name || "N/A"}</strong> |
              Doctor:{" "}
              <strong>{selectedAppointment.doctor?.name || "N/A"}</strong>
            </p>

            <label style={styles.modalLabel}>Prescription</label>
            <textarea
              style={styles.textarea}
              rows={4}
              value={prescriptionText}
              onChange={(e) => setPrescriptionText(e.target.value)}
              placeholder="Enter medicines, dosage, etc."
              disabled={user.role !== "doctor"}
            />

            <label style={styles.modalLabel}>Doctor Report / Notes</label>
            <textarea
              style={styles.textarea}
              rows={4}
              value={reportText}
              onChange={(e) => setReportText(e.target.value)}
              placeholder="Enter diagnosis, tests, advice, etc."
              disabled={user.role !== "doctor"}
            />

            <div style={styles.modalActions}>
              <button
                style={styles.buttonGhost}
                onClick={() => setSelectedAppointment(null)}
              >
                Close
              </button>
              {user.role === "doctor" && (
                <button style={styles.buttonPrimary} onClick={savePrescription}>
                  Save
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

/* ---------- STYLES ---------- */

const styles = {
  app: {
    minHeight: "100vh",
    fontFamily:
      "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    color: "#0f172a",
    background:
      "radial-gradient(circle at top left, #e0f2fe 0, #eef2ff 40%, #f9fafb 100%)",
    position: "relative",
  },
  gradientBg: {
    position: "fixed",
    inset: 0,
    background:
      "radial-gradient(circle at 0% 0%, rgba(59,130,246,0.18), transparent 55%)," +
      "radial-gradient(circle at 100% 0%, rgba(236,72,153,0.18), transparent 55%)",
    zIndex: -1,
  },
  header: {
    padding: "16px 32px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    backdropFilter: "blur(16px)",
    backgroundColor: "rgba(255,255,255,0.85)",
    borderBottom: "1px solid rgba(148,163,184,0.25)",
    position: "sticky",
    top: 0,
    zIndex: 20,
  },
  logoWrap: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  logoIcon: {
    width: 40,
    height: 40,
    borderRadius: 16,
    background: "linear-gradient(135deg,#2563eb,#4f46e5)",
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 22,
    boxShadow: "0 8px 24px rgba(37,99,235,0.45)",
  },
  title: {
    margin: 0,
    fontSize: 20,
    fontWeight: 700,
  },
  subtitle: {
    margin: 0,
    fontSize: 12,
    color: "#64748b",
  },
  userInfo: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  userChip: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 10px",
    borderRadius: 999,
    backgroundColor: "rgba(248,250,252,0.9)",
    border: "1px solid rgba(148,163,184,0.5)",
  },
  avatar: {
    width: 26,
    height: 26,
    borderRadius: "999px",
    background:
      "conic-gradient(from 180deg at 50% 50%, #38bdf8, #6366f1, #a855f7, #38bdf8)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    color: "white",
  },
  main: {
    maxWidth: 1200,
    margin: "24px auto 40px",
    padding: "0 16px",
  },
  splitLayout: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.3fr) minmax(0, 1fr)",
    gap: 24,
    alignItems: "stretch",
  },
  heroPanel: {
    padding: 24,
    borderRadius: 24,
    background:
      "linear-gradient(135deg, rgba(37,99,235,0.07), rgba(236,72,153,0.04))",
    border: "1px solid rgba(148,163,184,0.4)",
    boxShadow: "0 18px 45px rgba(15,23,42,0.12)",
  },
  heroTitle: {
    fontSize: 26,
    margin: "0 0 8px",
  },
  heroText: {
    margin: "0 0 16px",
    fontSize: 14,
    color: "#475569",
  },
  heroList: {
    margin: "0 0 18px",
    paddingLeft: 18,
    fontSize: 14,
    color: "#1e293b",
  },
  heroBadgeRow: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    marginTop: 6,
  },
  badge: {
    padding: "4px 10px",
    borderRadius: 999,
    background:
      "linear-gradient(135deg, rgba(37,99,235,0.9), rgba(59,130,246,0.9))",
    color: "white",
    fontSize: 11,
    fontWeight: 600,
  },
  badgeSoft: {
    padding: "4px 10px",
    borderRadius: 999,
    backgroundColor: "rgba(248,250,252,0.9)",
    border: "1px dashed rgba(148,163,184,0.8)",
    fontSize: 11,
    color: "#0f172a",
  },
  authPanel: {
    alignSelf: "stretch",
    display: "flex",
    alignItems: "center",
  },
  card: {
    width: "100%",
    borderRadius: 24,
    padding: 20,
    backgroundColor: "rgba(255,255,255,0.96)",
    boxShadow: "0 18px 45px rgba(15,23,42,0.14)",
    border: "1px solid rgba(148,163,184,0.28)",
    position: "relative",
    overflow: "hidden",
  },
  cardHeader: {
    marginBottom: 12,
  },
  cardSub: {
    margin: 4,
    fontSize: 13,
    color: "#6b7280",
  },
  roleTabs: {
    display: "flex",
    gap: 6,
    padding: 4,
    borderRadius: 999,
    backgroundColor: "#eef2ff",
    marginBottom: 12,
  },
  roleTab: {
    flex: 1,
    borderRadius: 999,
    border: "none",
    padding: "6px 0",
    fontSize: 12,
    cursor: "pointer",
    background: "transparent",
    color: "#4b5563",
  },
  roleTabActive: {
    background: "white",
    boxShadow: "0 2px 8px rgba(148,163,184,0.7)",
    color: "#111827",
    fontWeight: 600,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  input: {
    padding: "8px 10px",
    borderRadius: 10,
    border: "1px solid rgba(148,163,184,0.7)",
    fontSize: 13,
    outline: "none",
    backgroundColor: "#f9fafb",
  },
  buttonPrimary: {
    marginTop: 4,
    padding: "8px 14px",
    borderRadius: 999,
    border: "none",
    background: "linear-gradient(135deg, #2563eb, #4f46e5)",
    color: "#fff",
    fontWeight: 600,
    fontSize: 14,
    cursor: "pointer",
    boxShadow: "0 10px 25px rgba(37,99,235,0.45)",
  },
  buttonGhost: {
    padding: "7px 14px",
    borderRadius: 999,
    border: "1px solid rgba(148,163,184,0.85)",
    backgroundColor: "white",
    fontSize: 13,
    cursor: "pointer",
  },
  buttonGhostSm: {
    padding: "4px 10px",
    borderRadius: 999,
    border: "1px solid rgba(148,163,184,0.85)",
    backgroundColor: "white",
    fontSize: 11,
    cursor: "pointer",
  },
  buttonPrimarySm: {
    padding: "4px 10px",
    borderRadius: 999,
    border: "none",
    background: "linear-gradient(135deg,#22c55e,#16a34a)",
    color: "white",
    fontSize: 11,
    cursor: "pointer",
  },
  buttonDangerSm: {
    padding: "4px 10px",
    borderRadius: 999,
    border: "none",
    background: "linear-gradient(135deg,#f97373,#ef4444)",
    color: "white",
    fontSize: 11,
    cursor: "pointer",
  },
  switchText: {
    marginTop: 10,
    fontSize: 12,
    color: "#6b7280",
  },
  linkButton: {
    background: "none",
    border: "none",
    color: "#2563eb",
    textDecoration: "underline",
    cursor: "pointer",
    padding: 0,
    fontSize: 12,
  },
  toast: {
    position: "fixed",
    bottom: 12,
    left: "50%",
    transform: "translateX(-50%)",
    backgroundColor: "#0f172a",
    color: "white",
    padding: "8px 18px",
    borderRadius: 999,
    fontSize: 12,
  },
  dashboardWrap: {
    display: "flex",
    flexDirection: "column",
    gap: 18,
    marginTop: 8,
  },
  cardHero: {
    borderRadius: 22,
    padding: 20,
    background:
      "linear-gradient(135deg, rgba(59,130,246,0.06), rgba(79,70,229,0.04))",
    border: "1px solid rgba(148,163,184,0.3)",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 16,
  },
  welcomeLabel: {
    margin: 0,
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: 1,
    color: "#64748b",
  },
  welcomeName: {
    margin: "4px 0",
    fontSize: 22,
  },
  welcomeRole: {
    margin: 0,
    fontSize: 13,
    color: "#475569",
  },
  statsRow: {
    display: "flex",
    gap: 8,
  },
  statPill: {
    padding: "6px 10px",
    borderRadius: 14,
    backgroundColor: "rgba(15,23,42,0.9)",
    color: "white",
    fontSize: 11,
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  tabBar: {
    display: "inline-flex",
    padding: 4,
    borderRadius: 999,
    backgroundColor: "rgba(241,245,249,0.9)",
    border: "1px solid rgba(148,163,184,0.6)",
    alignSelf: "flex-start",
  },
  tabButton: {
    borderRadius: 999,
    border: "none",
    background: "transparent",
    padding: "6px 14px",
    fontSize: 12,
    cursor: "pointer",
    color: "#6b7280",
  },
  tabButtonActive: {
    backgroundColor: "white",
    boxShadow: "0 2px 10px rgba(148,163,184,0.8)",
    color: "#111827",
    fontWeight: 600,
  },
  modulesGrid: {
    marginTop: 12,
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },
  cardHeaderRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
  },
  cardTitle: {
    margin: 0,
    fontSize: 16,
  },
  pillBlue: {
    padding: "4px 8px",
    borderRadius: 999,
    backgroundColor: "rgba(59,130,246,0.08)",
    color: "#1d4ed8",
    fontSize: 11,
  },
  pillPurple: {
    padding: "4px 8px",
    borderRadius: 999,
    backgroundColor: "rgba(168,85,247,0.08)",
    color: "#7c3aed",
    fontSize: 11,
  },
  pillGreen: {
    padding: "4px 8px",
    borderRadius: 999,
    backgroundColor: "rgba(22,163,74,0.08)",
    color: "#15803d",
    fontSize: 11,
  },
  inlineForm: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: 12,
    marginTop: 6,
  },
  table: {
    marginTop: 4,
    borderRadius: 14,
    overflow: "hidden",
    border: "1px solid rgba(226,232,240,0.9)",
  },
  tableHeader: {
    display: "grid",
    padding: "8px 10px",
    backgroundColor: "#eef2ff",
    fontWeight: 600,
    fontSize: 12,
  },
  tableRow: {
    display: "grid",
    padding: "8px 10px",
    fontSize: 12,
    alignItems: "center",
    backgroundColor: "white",
    borderTop: "1px solid rgba(226,232,240,0.8)",
  },
  emptyState: {
    padding: 14,
    textAlign: "center",
    fontSize: 12,
    color: "#94a3b8",
    backgroundColor: "rgba(248,250,252,0.9)",
  },
  profileGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 12,
    marginTop: 10,
  },
  profileLabel: {
    margin: 0,
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    color: "#94a3b8",
  },
  profileValue: {
    margin: "2px 0 0",
    fontSize: 13,
  },
  helperText: {
    marginTop: 10,
    fontSize: 11,
    color: "#6b7280",
  },
  tagLight: {
    marginLeft: 6,
    padding: "2px 6px",
    borderRadius: 999,
    backgroundColor: "#eff6ff",
    fontSize: 10,
    color: "#1d4ed8",
  },
  mutedText: {
    fontSize: 11,
    color: "#9ca3af",
  },
  modalBackdrop: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(15,23,42,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 40,
  },
  modal: {
    width: "min(560px, 90%)",
    backgroundColor: "white",
    borderRadius: 18,
    padding: 18,
    boxShadow: "0 30px 70px rgba(15,23,42,0.7)",
  },
  modalSubtitle: {
    margin: "4px 0 12px",
    fontSize: 12,
    color: "#6b7280",
  },
  modalLabel: {
    display: "block",
    marginTop: 8,
    marginBottom: 4,
    fontSize: 12,
    color: "#4b5563",
  },
  textarea: {
    width: "100%",
    borderRadius: 10,
    border: "1px solid rgba(148,163,184,0.9)",
    padding: 8,
    fontSize: 13,
    resize: "vertical",
    backgroundColor: "#f9fafb",
  },
  modalActions: {
    marginTop: 12,
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
  },
};

export default App;
